import React from "react"
import { ServicePageLayout } from "../../components/services/ServicePageLayout"
import { METADATA } from "../../components/meta/metadata"

export default function OperationalAssessmentPage() {
  return (
    <ServicePageLayout
      title={METADATA.operationalAssessment.title}
      metaDescription={METADATA.operationalAssessment.description}
      backgroundImage="/images/backgrounds/flickr-365822321.jpg"
      backgroundAlt="Operational Assessment"
      headline="Operational Assessment:<br />Understand Before You Transform"
      tagline="Before implementing AI, understand where you are. Systematic discovery of current-state challenges with root cause analysis."
      introContent={[
        "Many AI initiatives fail because they solve the wrong problems. Teams implement technology without understanding what is actually causing operational friction. Money is spent, tools are deployed and the underlying issues remain.",
        "Operational Assessment provides the clarity you need. We systematically identify pain points, trace them to root causes and deliver leadership-ready insights that form the foundation for informed decision-making."
      ]}
      deliverables={[
        {
          title: "Structured stakeholder engagement",
          description: "Interviews and workshops with key personnel across your organisation to understand operational reality from multiple perspectives"
        },
        {
          title: "Pain points analysis",
          description: "Systematic identification of operational challenges, inefficiencies and friction points that impact performance"
        },
        {
          title: "Root cause identification",
          description: "Analysis that goes beyond symptoms to identify underlying causes - what is actually driving the problems you see"
        },
        {
          title: "Process and knowledge flow mapping",
          description: "Documentation of how work actually happens and where knowledge gets stuck or lost"
        },
        {
          title: "Leadership-ready insights report",
          description: "Synthesised findings with actionable recommendations, prioritised by impact and feasibility"
        }
      ]}
      processSteps={[
        {
          title: "Discovery Phase",
          description: "Stakeholder interviews, process observation and document review. We learn how your organisation actually operates, not just how it is supposed to operate."
        },
        {
          title: "Enrichment Phase",
          description: "Analysis, pattern identification and root cause mapping. We connect the dots between symptoms and causes, identifying systemic issues."
        },
        {
          title: "Delivery Phase",
          description: "Synthesised insights presented in leadership-ready format. Clear findings, prioritised recommendations, foundation for next steps."
        }
      ]}
      benefits={[
        {
          title: "Foundation for Prioritisation",
          description: "Evidence-based understanding of where problems actually lie. Prevents investing in solutions to the wrong problems."
        },
        {
          title: "Identifies Quick Wins",
          description: "Alongside strategic opportunities, we identify improvements that can deliver value immediately with minimal investment."
        },
        {
          title: "Creates Shared Understanding",
          description: "Leadership teams gain common view of operational reality. Alignment on problems precedes alignment on solutions."
        },
        {
          title: "Precursor to AI Investment",
          description: "Clear understanding of where AI can genuinely add value, versus where simpler solutions might be more appropriate."
        }
      ]}
      audience={[
        "Organisations seeking clarity on current-state challenges",
        "Leadership preparing for transformation initiatives",
        "Teams unsure where AI would add most value",
        "Organisations wanting evidence-based prioritisation",
        "Precursor to AI Opportunity Identification or AI Kickstarter"
      ]}
      relatedServices={[
        {
          name: "AI Opportunity Identification",
          description: "Natural follow-on from Operational Assessment - identify where AI creates value",
          href: "/services/ai-opportunity"
        },
        {
          name: "AI Kickstarter",
          description: "8-week programme that includes assessment as Phase 1",
          href: "/services/ai-kickstarter"
        }
      ]}
      ctaHeadline="Ready to Understand Your Operations?"
      ctaDescription="Let's discuss how Operational Assessment can provide the clarity your organisation needs."
      ctaButtonText="Schedule a Consultation"
    />
  )
}
